import { ApiClient } from "./api"
import { resolveAttribution } from "./attribution"
import { assessPhoto } from "./photo-check"
import { discoverProduct, normalizeProduct } from "./discovery"
import { styles } from "./styles"
import { template } from "./template"
import { normalizePhoto } from "./image"
import { canShareFiles, isAbort, isIOS } from "./device"
import { humanError } from "./copy"
import * as Pending from "./pending"
import * as SizePanel from "./size-panel"
import type { GenerationResponse, ProductContext, SizeResultData, WidgetConfig } from "./types"
import { STRINGS, type Strings } from "./strings"

export class TryOnWidget extends HTMLElement {
  private config!: WidgetConfig
  private api!: ApiClient
  private product: ProductContext | null = null
  private shadow!: ShadowRoot
  private front: File | null = null
  private side: File | null = null
  private previewUrls: Partial<Record<"front" | "side", string>> = {}
  private resultObjectUrl: string | null = null
  private resultBlob: Blob | null = null
  private lastFocused: HTMLElement | null = null
  private runId = 0
  private pending: "idle" | "generating" | "ready" | "error" = "idle"
  private photoBlocked = false // a headshot/no-body front photo gates generation
  private tryOnEnabled = true // operator kill switch; apparel_try_on=false hides the widget (fail open)
  private placed = true // the loader's placement clears this when the page offers no good spot
  private reportedUnplaced: string | null = null
  private lastError = ""
  private toastDismissed = false
  private currentTryOnId: string | number | null = null
  private pendingResultUrl: string | null = null
  private pendingProduct: { name: string; url: string } | null = null
  private configPromise?: Promise<void>
  private visitor = persistentId("vc_visitor", localStorage)
  private session = persistentId("vc_session", sessionStorage)
  private attribution = resolveAttribution(window)

  private s: Strings = STRINGS.es

  configure(config: WidgetConfig) {
    this.config = config
    this.s = STRINGS[config.lang]
    this.api = new ApiClient(config, this.visitor, this.session, this.attribution)
    this.style.setProperty("--vc-primary", config.primaryColor)
  }

  connectedCallback() {
    if (this.shadowRoot || !this.config) return
    this.shadow = this.attachShadow({ mode: "open" })
    this.shadow.innerHTML = `<style>${styles}</style>${template(this.config, this.s)}`
    this.bind()
    this.refreshProduct()
    this.renderCta()
    this.resumePending()
    // Prefetch capability flags so the size field's visibility is settled well
    // before the shopper opens the modal.
    void this.ensureConfig()
  }

  // The inline CTA is scoped to THIS page's product. It only reflects try-on
  // state when the running/finished job was launched from this same product;
  // otherwise it stays the plain "try it on" invitation. A job for a different
  // product lives on the floating pill, not on this button — so the CTA never
  // shows another product's "Reintentar"/"processing" state. On pages with no
  // product the button is hidden entirely (only the pill can appear).
  private renderCta() {
    const cta = this.shadow.querySelector<HTMLButtonElement>("[data-open]")
    if (!cta) return
    cta.hidden = !this.product || !this.tryOnEnabled || !this.placed
    const reflect = this.pending !== "idle" && this.jobIsForCurrentProduct()
    const state = reflect ? this.pending : "idle"
    cta.dataset.state = state
    const labels: Record<"idle" | "generating" | "ready" | "error", string> = {
      idle: this.config.ctaLabel,
      generating: this.s.ctaGenerating,
      ready: this.s.ctaReady,
      error: this.s.ctaError
    }
    cta.textContent = labels[state]
  }

  hasProduct() {
    return Boolean(this.product)
  }

  // Set by the loader's placement. A CTA with no good spot on the page stays hidden
  // rather than landing, detached, at the end of <body>.
  setPlaced(placed: boolean) {
    this.placed = placed
    this.renderCta()
  }

  // A real product page offered no spot for the CTA: tell the operator, because the
  // shopper can no longer see the problem. Once per page, however often it re-runs.
  reportUnplaced() {
    const page = location.origin + location.pathname
    if (!this.product || !this.tryOnEnabled || this.placed || this.reportedUnplaced === page) return
    this.reportedUnplaced = page
    this.api.track("cta_unplaced", this.product, { properties: { page_url: page } })
  }

  // Is the tracked background job the one for the product on this page?
  private jobIsForCurrentProduct() {
    const url = this.pendingProduct?.url
    return Boolean(url && this.product && samePage(url, this.product.canonicalUrl))
  }

  // A try-on started on a previous page (same tab) survives navigation: its id is
  // in sessionStorage, so a fresh page picks it back up — resuming the poll if it
  // is still running, or surfacing the ready/error pill so the shopper never
  // loses a result just because they kept browsing.
  private resumePending() {
    const saved = this.readPending()
    if (!saved) return
    this.currentTryOnId = saved.id
    this.pendingProduct = saved.product || null
    if (saved.status === "ready") {
      this.pendingResultUrl = saved.resultUrl || null
      this.setPending("ready")
    } else if (saved.status === "error") {
      this.lastError = saved.message || humanError("generation_failed", this.s)
      this.setPending("error")
    } else {
      this.setPending("generating")
      void this.resumePoll(saved.id)
    }
  }

  private async resumePoll(id: string | number) {
    try {
      const result = await this.api.pollGeneration(id)
      if (this.currentTryOnId !== id) return // superseded by a newer try-on
      await this.settleTryOn(result)
    } catch (error) {
      if (this.currentTryOnId !== id) return
      this.failTryOn(error)
    }
  }

  // The inline CTA. If a job for THIS product is in flight/finished, reopen it;
  // otherwise start a fresh try-on for this product (without disturbing a job
  // running for a different product).
  async open() {
    if (this.pending !== "idle" && this.jobIsForCurrentProduct()) { this.openPending(); return }

    if (!this.product) this.refreshProduct()
    if (!this.product) return this.showError(this.s.errNoProduct)
    // Reconcile the cockpit's capability flags before showing the form, so a
    // disabled size_recommendation hides the height field (usually already
    // resolved from the boot prefetch — awaited here only as a backstop).
    await this.ensureConfig()
    // Kill switch honored for the headless API too (window.VisualCommerce.open()):
    // if try-on is disabled server-side, open nothing.
    if (!this.tryOnEnabled) return
    this.resetForm()
    this.applySizeVisibility() // re-evaluate for THIS product (hide height for footwear/bags/backpacks)
    this.reveal("upload")
    this.api.track("capability_opened", this.product)
  }

  // Fetch the merchant's enabled capabilities once per widget lifetime and apply
  // them to the running config. Prefetched at connect so open() rarely waits.
  private ensureConfig(): Promise<void> {
    return (this.configPromise ??= this.loadConfig())
  }

  private async loadConfig() {
    try {
      const capabilities = await this.api.fetchConfig()
      // The cockpit toggle is authoritative for enabling; a merchant that set
      // data-size-enabled="false" on the embed can still keep it locally off.
      this.config.sizeEnabled = this.config.sizeEnabled && capabilities.size_recommendation !== false
      // Combined delivery: size rides the try-on call, so no separate size request
      // and no side photo. Only meaningful while size is enabled.
      this.config.sizeCombined = this.config.sizeEnabled && capabilities.size_delivered_with_tryon === true
      // Operator kill switch: a suspended / unpaid / non-operable merchant reports
      // apparel_try_on=false, so the whole widget goes dark (CTA hidden) instead of
      // offering a button that errors on submit. Only an EXPLICIT false from a
      // reachable server disables — a missing flag or a boot hiccup stays enabled
      // (fail open), which is why this sits inside the try and defaults true.
      this.tryOnEnabled = capabilities.apparel_try_on !== false
    } catch {
      // Config unreachable: keep the embed's static default (fail open to the
      // pre-existing behavior) rather than break the widget on a boot hiccup.
    }
    this.applySizeVisibility()
    this.renderCta()
  }

  // Footwear, bags and backpacks are visualization-only — the server never sizes
  // them, so the height field must not be shown or sent. The server classifies by
  // product name; this mirrors it client-side for the UI (server stays authoritative).
  private static readonly NON_SIZEABLE =
    /(zapatill|zapato|calzado|\bbota|botin|borcego|sandalia|ojota|alpargata|mocasin|pantufla|sneaker|boot|\bshoe|loafer|slipper|sandal|cartera|bolso|bolsa|bandoler|rinoner|cartuchera|neceser|\btote|clutch|handbag|purse|mochila|backpack|morral)/i

  private isVisualizationOnlyProduct(): boolean {
    const text = `${this.product?.name ?? ""} ${this.product?.category ?? ""}`.toLowerCase()
    return TryOnWidget.NON_SIZEABLE.test(text)
  }

  private applySizeVisibility() {
    const fields = this.shadow.querySelector<HTMLElement>("[data-size-fields]")
    if (fields) fields.hidden = !this.config.sizeEnabled || this.isVisualizationOnlyProduct()
    // The side photo only feeds the independent (self_hosted) size call; when size
    // rides the try-on call there is nowhere to send it, so hide it.
    const sidePhoto = this.shadow.querySelector<HTMLElement>("[data-side-photo]")
    if (sidePhoto) sidePhoto.hidden = this.config.sizeCombined
  }

  // The floating pill / cross-product surface: reopen the tracked job's state
  // regardless of which product page we're currently on.
  private openPending() {
    if (this.pending === "generating") { this.reveal("loading"); return }
    if (this.pending === "ready") {
      this.api.track("result_viewed", this.product, {})
      void this.showResult(this.pendingResultUrl)
      this.clearPending() // consumed on view — stops the pill nagging on later pages
      this.setPending("idle")
      return
    }
    if (this.pending === "error") {
      this.showError(this.lastError || humanError("generation_failed", this.s))
      this.clearPending()
      this.setPending("idle")
    }
  }

  close() {
    this.modal().hidden = true
    this.renderToast()
    // Return focus to whatever opened the modal (CTA / pill), so keyboard and
    // screen-reader users are not dropped at the top of the page.
    this.lastFocused?.focus?.()
    this.lastFocused = null
  }

  // Show the modal on a given step and keep the floating toast in sync.
  private reveal(step: string) {
    const modal = this.modal()
    // Remember the opener only on the transition from closed → open.
    if (modal.hidden) this.lastFocused = (this.shadow.activeElement as HTMLElement) || null
    modal.hidden = false
    this.show(step)
    this.renderToast()
    // Move focus to this step's heading: it both announces the new state to a
    // screen reader and lands keyboard focus inside the dialog.
    this.shadow.querySelector<HTMLElement>(`[data-step="${step}"] h2`)?.focus()
  }

  private onModalKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") { event.preventDefault(); this.close(); return }
    if (event.key !== "Tab") return
    const card = this.shadow.querySelector(".card")
    if (!card) return
    const focusables = [ ...card.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    ) ].filter((el) => el.offsetParent !== null)
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = this.shadow.activeElement as HTMLElement | null
    if (event.shiftKey && active === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus() }
  }

  // Shopper-initiated share of the result. Prefers the native share sheet with
  // the actual image (mobile: WhatsApp is one tap away); falls back to a WhatsApp
  // link with the product URL. Never automatic — always a button press.
  private async shareResult() {
    const productUrl = this.pendingProduct?.url || this.product?.canonicalUrl || location.href
    const file = this.resultFile()

    // Best path (mobile): native share sheet WITH the image attached — one tap to
    // WhatsApp/Instagram/etc. The product URL rides inside `text` rather than the
    // separate `url` field, which some targets drop when files are present.
    if (file && canShareFiles(file)) {
      try {
        await navigator.share({ files: [ file ], text: this.s.shareText(productUrl) })
        this.api.track("result_shared", this.product, { properties: { channel: "native" } })
        return
      } catch (error) {
        // Only a real deliberate cancel stops here; any other failure falls
        // through to the WhatsApp link so the button never dead-ends silently.
        if (isAbort(error)) return
      }
    }

    // Fallback (desktop / no file share): WhatsApp with text + product link. The
    // image can't ride a wa.me URL, so this shares the look as a link, not a photo.
    const text = encodeURIComponent(this.s.shareText(productUrl))
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener")
    this.api.track("result_shared", this.product, { properties: { channel: "whatsapp_link" } })
  }

  private async saveResult() {
    const file = this.resultFile()

    // iOS Safari ignores the <a download> attribute and just navigates to the blob
    // (opening the image, not saving it). The native share sheet is the reliable
    // "Guardar imagen / Save to Photos" path there — prefer it on iOS.
    if (file && isIOS() && canShareFiles(file)) {
      try {
        await navigator.share({ files: [ file ] })
        this.api.track("result_saved", this.product, { properties: { channel: "ios_share" } })
        return
      } catch (error) {
        if (isAbort(error)) return
        // fall through to the download / open-in-tab fallback below
      }
    }

    if (!this.resultObjectUrl) return
    const link = document.createElement("a")
    link.href = this.resultObjectUrl
    link.download = "probador-virtual.webp"
    link.rel = "noopener"
    link.click()
    this.api.track("result_saved", this.product)
  }

  // The generated result as a File, ready for the Web Share API. Null until the
  // result blob has been downloaded.
  private resultFile() {
    return this.resultBlob
      ? new File([ this.resultBlob ], "probador-virtual.webp",
        { type: this.resultBlob.type || "image/webp" })
      : null
  }
  getProduct() { return this.product }

  // Emit the funnel's conversion event. Called by the loader when the store's
  // add-to-cart control is clicked, and exposed on window.VisualCommerce for
  // merchants whose cart is custom (SPA re-render, headless checkout). Silent
  // no-op off a product page — there is nothing to attribute.
  trackAddToCart(product?: ProductContext | null) {
    const target = product ? normalizeProduct(product) : this.product
    if (!target) return
    this.api.track("add_to_cart", target, {
      value_cents: target.priceCents,
      currency: target.currency
    })
  }

  refreshProduct(product?: ProductContext | null) {
    this.product = product ? normalizeProduct(product) : discoverProduct(this.config.getProduct)
    if (this.product) this.api.track("product_view", this.product)
    return this.product
  }

  private bind() {
    this.shadow.querySelector<HTMLButtonElement>("[data-open]")!.onclick = () => this.open()
    this.shadow.querySelectorAll<HTMLElement>("[data-close]").forEach((el) => el.onclick = () => this.close())
    this.shadow.querySelector<HTMLButtonElement>("[data-generate]")!.onclick = () => void this.generate()
    this.shadow.querySelectorAll<HTMLButtonElement>("[data-retry]").forEach((button) => {
      button.onclick = () => this.retryFlow()
    })
    this.shadow.querySelectorAll<HTMLButtonElement>("[data-delete]").forEach((button) => {
      button.onclick = () => void this.deletePhotos()
    })
    this.shadow.querySelector<HTMLButtonElement>("[data-share]")!.onclick = () => void this.shareResult()
    this.shadow.querySelector<HTMLButtonElement>("[data-save]")!.onclick = () => this.saveResult()
    // Keyboard accessibility for the modal: Escape closes, Tab is trapped inside
    // the dialog so focus never leaks to the page behind it.
    this.modal().addEventListener("keydown", (event) => this.onModalKeydown(event))
    this.shadow.querySelector<HTMLInputElement>("[data-front]")!.onchange = (event) => {
      this.front = (event.target as HTMLInputElement).files?.[0] || null
      this.preview("front", this.front)
      this.ready()
      this.checkPhotoQuality(this.front)
    }
    this.shadow.querySelector<HTMLInputElement>("[data-side]")!.onchange = (event) => {
      this.side = (event.target as HTMLInputElement).files?.[0] || null
      this.preview("side", this.side)
    }
    this.shadow.querySelector<HTMLInputElement>("[data-height]")!.oninput = () => this.ready()
    this.shadow.querySelector<HTMLButtonElement>("[data-toast-open]")!.onclick = () => this.openPending()
    this.shadow.querySelector<HTMLButtonElement>("[data-toast-dismiss]")!.onclick = (event) => {
      event.stopPropagation()
      this.toastDismissed = true
      this.renderToast()
    }
    // A soft (SPA) product change just re-scopes the CTA to the new product. The
    // tracked job (and its pill) is deliberately left intact.
    window.addEventListener("visual-commerce:product-change", (event) => {
      this.refreshProduct(event.detail)
      this.renderCta()
    })
  }

  private async generate() {
    if (!this.front || !this.product || !this.photoConsent()) return
    if (this.photoBlocked) {
      return this.showError(this.s.errHeadshot)
    }
    // One try-on per shopper at a time. Block a second start honestly instead of
    // letting the backend reject it — the running one keeps going on the pill.
    if (this.pending === "generating") {
      return this.showError(this.s.errInProgress)
    }
    const runId = ++this.runId
    const height = Number(this.shadow.querySelector<HTMLInputElement>("[data-height]")!.value)
    // Know the origin product up front so the pill/CTA name it from the first frame.
    this.pendingProduct = this.originProduct() || null
    const wantsSize = this.config.sizeEnabled && !this.isVisualizationOnlyProduct() && height >= 120 && height <= 220
    // Combined only when the account seizes the try-on call AND there is no side
    // photo (a side photo has extra evidence only the separate call can use).
    const combined = wantsSize && this.config.sizeCombined && !this.side
    this.setPending("generating")
    this.show("loading")
    if (wantsSize) this.renderSizeLoading()
    try {
      const front = await normalizePhoto(this.front)
      const side = this.side ? await normalizePhoto(this.side) : null

      // Separated path (self_hosted, or a side photo present): a parallel call.
      const sizePromise = wantsSize && !combined
        ? this.api.size(front, side, height, this.product, this.visitor, this.session)
          .then((response) => {
            if (this.runId !== runId) return
            this.renderSize(response)
            this.trackSize(response)
          })
          .catch((error) => {
            if (this.runId === runId) this.renderSizeUnavailable(error)
          })
        : Promise.resolve()

      const result = await this.api.tryOn(
        front, this.product, this.visitor, this.session,
        (created) => this.markTryOnCreated(created),
        combined ? { heightCm: height } : undefined
      )
      if (this.runId !== runId) return
      await this.settleTryOn(result)

      // Combined size rides the try-on response; a drifted/unknown verdict is shown
      // softly and never undoes the delivered try-on.
      if (combined) {
        this.renderSize(result)
        this.trackSize(result, { combined: true })
      }
      await sizePromise
    } catch (error) {
      if (this.runId !== runId) return
      this.failTryOn(error)
    }
  }

  private trackSize(response: GenerationResponse, extra: Record<string, unknown> = {}) {
    const data = response.result_data
    this.api.track("size_completed", this.product, {
      generation_id: response.id,
      size_status: data?.size_status,
      overall_confidence: data?.overall_confidence,
      ...extra
    })
  }

  // Persist the id the moment the generation exists, so a navigation mid-poll can
  // resume it on the next page.
  private markTryOnCreated(created: GenerationResponse) {
    this.currentTryOnId = created.id
    this.pendingProduct = this.originProduct() || null
    Pending.save({ id: created.id, status: "generating", product: this.originProduct() })
  }

  // The product the try-on was launched from, so the result — viewed from any
  // page — can link back to it.
  private originProduct() {
    return this.product ? { name: this.product.name, url: this.product.canonicalUrl } : undefined
  }

  // Terminal-state handling shared by the foreground flow and the resume-poll.
  // Throws for a non-success result so the caller's catch routes it to failTryOn.
  private async settleTryOn(result: GenerationResponse) {
    if (result.status !== "succeeded" || !result.result_url) {
      throw new Error(result.failure_class || "generation_failed")
    }
    const resultImage = await this.api.resultImage(result.result_url)
    if (this.currentTryOnId !== result.id) return // superseded by a newer try-on
    this.releaseResultImage()
    this.resultBlob = resultImage
    this.resultObjectUrl = URL.createObjectURL(resultImage)
    this.shadow.querySelector<HTMLImageElement>("[data-result]")!.src = this.resultObjectUrl
    this.currentTryOnId = result.id
    this.pendingResultUrl = result.result_url
    // Preserve the ORIGIN product. When a resume-poll finishes on a page the
    // shopper navigated to, this.product is that other page — not where the
    // try-on was launched. The stored/pending origin must win.
    const origin = this.pendingProduct || this.originProduct() || null
    this.pendingProduct = origin
    this.storePending({
      id: result.id, status: "ready", resultUrl: result.result_url,
      product: origin || undefined
    })
    this.setPending("ready")
    this.api.track("result_ready", this.product, { generation_id: result.id })
    // Only pull the shopper into the result if they stayed on the widget.
    // Otherwise the pill/CTA signal "ready" and reopening (any page) shows it.
    if (this.isModalOpen()) {
      this.renderBackLink()
      this.show("result")
      this.api.track("result_viewed", this.product, { generation_id: result.id })
      this.clearPending()
      this.setPending("idle") // consumed in place — no stale pill after closing
    }
  }

  private failTryOn(error: unknown) {
    const message = humanError(error instanceof Error ? error.message : "generation_failed", this.s)
    this.lastError = message
    this.storePending({ id: this.currentTryOnId, status: "error", message })
    // Always leave a recoverable state — never stuck on "generating".
    this.setPending("error")
    if (this.isModalOpen()) this.showError(message)
  }

  private async showResult(url: string | null) {
    // Render the back-link first: it reads the origin product, which the caller
    // may clear synchronously while the image below is still downloading.
    this.renderBackLink()
    if (!this.resultObjectUrl && url) {
      try {
        const image = await this.api.resultImage(url)
        this.releaseResultImage()
        this.resultBlob = image
        this.resultObjectUrl = URL.createObjectURL(image)
        this.shadow.querySelector<HTMLImageElement>("[data-result]")!.src = this.resultObjectUrl
      } catch (error) {
        return this.showError(humanError(error instanceof Error ? error.message : "generation_failed", this.s))
      }
    }
    this.reveal("result")
  }

  // When the result is viewed from a different page than the one the try-on was
  // launched from, offer a link back to that product.
  private renderBackLink() {
    const link = this.shadow.querySelector<HTMLAnchorElement>("[data-back-to-product]")
    if (!link) return
    const origin = this.pendingProduct || (this.product
      ? { name: this.product.name, url: this.product.canonicalUrl } : null)
    const elsewhere = Boolean(origin?.url) && !samePage(origin!.url, location.href)
    link.hidden = !elsewhere
    if (elsewhere) {
      link.href = origin!.url
      link.textContent = origin!.name ? this.s.backToNamed(origin!.name) : this.s.backToProduct
    }
  }

  private storePending(data: { id: string | number | null; status: "generating" | "ready" | "error"; resultUrl?: string; message?: string; product?: { name: string; url: string } }) {
    Pending.save(data)
  }

  private readPending() {
    return Pending.load()
  }

  private clearPending() {
    this.pendingResultUrl = null
    this.pendingProduct = null
    Pending.remove()
  }

  private preview(kind: "front" | "side", file: File | null) {
    const image = this.shadow.querySelector<HTMLImageElement>(`[data-${kind}-preview]`)!
    // Revoke the previous preview's object URL before replacing it — otherwise a
    // shopper re-picking photos leaks a blob for the tab's lifetime.
    this.releasePreview(kind, image)
    image.hidden = !file
    if (file) {
      const url = URL.createObjectURL(file)
      this.previewUrls[kind] = url
      image.src = url
    }
  }

  private releasePreview(kind: "front" | "side", image?: HTMLImageElement | null) {
    const url = this.previewUrls[kind]
    if (!url) return
    URL.revokeObjectURL(url)
    delete this.previewUrls[kind]
    ;(image ?? this.shadow.querySelector<HTMLImageElement>(`[data-${kind}-preview]`))?.removeAttribute("src")
  }

  // Honest, non-blocking pre-generation feedback. Guides a retake on a weak photo
  // but never prevents the shopper from continuing. A stale check from a slower
  // file is ignored so only the latest selection is reflected.
  private photoCheckRun = 0
  private async checkPhotoQuality(file: File | null) {
    const hint = this.shadow.querySelector<HTMLParagraphElement>("[data-photo-hint]")!
    if (!file) { this.photoBlocked = false; hint.hidden = true; hint.textContent = ""; this.ready(); return }
    const run = ++this.photoCheckRun
    const verdict = await assessPhoto(file, this.s)
    if (run !== this.photoCheckRun) return
    // A headshot/no-body photo gates generation; advisory verdicts never do.
    this.photoBlocked = verdict.level === "block"
    this.ready()
    if (!verdict.hint) { hint.hidden = true; hint.textContent = ""; return }
    hint.hidden = false
    hint.dataset.level = verdict.level
    hint.textContent = verdict.hint
  }

  private ready() {
    const height = Number(this.shadow.querySelector<HTMLInputElement>("[data-height]")!.value)
    // Visualization-only products (footwear/bags/backpacks) hide the height field, so
    // they must never require a height to be "ready" — otherwise the generate button
    // stays disabled for a size-enabled account.
    const sizeReady = !this.config.sizeEnabled || this.isVisualizationOnlyProduct() || (height >= 120 && height <= 220)
    this.shadow.querySelector<HTMLButtonElement>("[data-generate]")!.disabled =
      !(this.front && sizeReady && this.photoConsent() && !this.photoBlocked)
  }

  private renderSizeLoading() {
    SizePanel.loading(this.shadow, this.s)
  }

  private renderSize(response: GenerationResponse) {
    SizePanel.render(this.shadow, response, this.s)
  }

  private renderSizeUnknown(reason: string | null | undefined) {
    SizePanel.unknown(this.shadow, reason, this.s)
  }

  private renderSizeUnavailable(error: unknown) {
    SizePanel.unavailable(this.shadow, error, this.s)
  }

  private async deletePhotos() {
    this.shadow.querySelectorAll<HTMLButtonElement>("[data-delete]").forEach((button) => {
      button.disabled = true
    })
    try {
      await this.api.deleteShopperPhotos()
      this.reset()
      this.modal().hidden = false
      this.show("deleted")
    } catch (_error) {
      this.shadow.querySelectorAll<HTMLButtonElement>("[data-delete]").forEach((button) => {
        button.disabled = false
      })
      this.showError(this.s.errDeleteFailed)
    }
  }

  // Clears only the modal's own inputs/preview — NOT the tracked global job. Used
  // when opening a fresh upload while a try-on for another product still runs.
  private resetForm() {
    this.releaseResultImage()
    this.releasePreview("front")
    this.releasePreview("side")
    this.front = null
    this.side = null
    this.shadow.querySelectorAll<HTMLInputElement>('input[type="file"]').forEach((input) => { input.value = "" })
    this.shadow.querySelector<HTMLInputElement>("[data-height]")!.value = ""
    this.shadow.querySelectorAll<HTMLImageElement>("[data-front-preview], [data-side-preview]").forEach((image) => { image.hidden = true })
    this.checkPhotoQuality(null)
    this.shadow.querySelector<HTMLButtonElement>("[data-generate]")!.disabled = true
    this.shadow.querySelectorAll<HTMLButtonElement>("[data-delete]").forEach((button) => {
      button.disabled = false
    })
    SizePanel.hide(this.shadow)
  }

  // Full reset: the form AND the tracked job (used when starting over on the same
  // product, or after deleting photos).
  private reset() {
    this.runId += 1
    this.resetForm()
    this.lastError = ""
    this.currentTryOnId = null
    this.clearPending()
    this.setPending("idle")
  }

  // "Probar otra foto" / "Intentar de nuevo": go back to this product's upload.
  // Only clear the tracked job if it belongs to this product (never wipe a job
  // that is still running for a different product).
  private retryFlow() {
    if (this.pending === "idle" || this.jobIsForCurrentProduct()) this.reset()
    else this.resetForm()
    this.reveal("upload")
  }

  // The single source of the background job's state. It drives two surfaces —
  // the product-scoped CTA and the global pill — each rendered for its own scope.
  private setPending(state: "idle" | "generating" | "ready" | "error") {
    this.pending = state
    // Each new state re-offers the floating pill (a previous dismissal is stale).
    this.toastDismissed = false
    this.renderCta()
    this.renderToast()
  }

  // Floating, viewport-fixed status pill — the cross-product surface, visible on
  // any page (even one with no product) so a backgrounded result is never missed.
  // Names the product so it's clear which garment is cooking while browsing others.
  private renderToast() {
    const toast = this.shadow.querySelector<HTMLElement>("[data-toast]")
    if (!toast) return
    const active = this.pending === "generating" || this.pending === "ready" || this.pending === "error"
    const shouldShow = active && !this.isModalOpen() && !this.toastDismissed
    toast.hidden = !shouldShow
    if (!shouldShow) return

    const state = this.pending as "generating" | "ready" | "error"
    toast.dataset.state = state
    const name = this.pendingProduct?.name
    const copy: Record<"generating" | "ready" | "error", { title: string; action: string }> = {
      generating: { title: this.s.toastGeneratingTitle(name), action: this.s.toastGeneratingAction },
      ready: { title: this.s.toastReadyTitle(name), action: this.s.toastReadyAction },
      error: { title: this.s.toastErrorTitle, action: this.s.toastErrorAction }
    }
    this.shadow.querySelector<HTMLElement>("[data-toast-spinner]")!.hidden = state !== "generating"
    this.shadow.querySelector<HTMLElement>("[data-toast-title]")!.textContent = copy[state].title
    this.shadow.querySelector<HTMLElement>("[data-toast-action]")!.textContent = copy[state].action
  }

  private isModalOpen() { return !this.modal().hidden }

  private releaseResultImage() {
    if (this.resultObjectUrl) URL.revokeObjectURL(this.resultObjectUrl)
    this.resultObjectUrl = null
    this.resultBlob = null
    const image = this.shadow?.querySelector<HTMLImageElement>("[data-result]")
    if (image) image.removeAttribute("src")
  }

  private show(step: string) {
    this.shadow.querySelectorAll<HTMLElement>("[data-step]").forEach((el) => { el.hidden = el.dataset.step !== step })
  }

  private showError(message: string) {
    this.modal().hidden = false
    this.shadow.querySelector<HTMLElement>("[data-error]")!.textContent = message
    this.show("error")
    this.renderToast()
  }

  private modal() { return this.shadow.querySelector<HTMLElement>("[data-modal]")! }
  // Consent is implied by the act of uploading (microcopy states it); we always
  // send it to the API, which records the consent version server-side.
  private photoConsent() { return true }
}

function samePage(a: string, b: string) {
  try {
    const ua = new URL(a, location.href)
    const ub = new URL(b, location.href)
    return ua.origin === ub.origin && ua.pathname === ub.pathname
  } catch (_error) {
    return a === b
  }
}

function persistentId(key: string, storage: Storage) {
  let value = storage.getItem(key)
  if (!value) {
    value = crypto.randomUUID?.() || Math.random().toString(36).slice(2)
    storage.setItem(key, value)
  }
  return value
}
